// "Enviar para o My Planner?" (texto 10, E): a pergunta ao criar um item com data, o aviso pequeno embaixo da tela
// (só dentro do app), a lista de itens aguardando envio, "Escolher quando" e a data do "Me lembre de confirmar".
// Montado uma vez na raiz do app (app/_layout.tsx). A lógica fica em lib/envioPlanner.ts e lib/envioEstado.ts.
import { useEffect, useState } from 'react';
import { AppState, Modal, Pressable, ScrollView, Text, View } from 'react-native';
import type { AbaId } from '../store/types';
import { useDoc } from '../store/store';
import { abrirListaEnvios, conferirAvisoEnvio, depois, enviarAgora, escolherQuando, existe, fecharAvisoEnvio, fecharPedidoEnvio, lembrarDepois,
  useAvisoEnvio, useListaEnvios, usePedidoEnvio, type PedidoEnvio } from '../lib/envioPlanner';
import { confirmarItem, encerrarPendente, lembrarDeConfirmar, lembrarEscolha, pedirDataConfirmar, usePedidoConfirmar } from '../lib/envioEstado';
import { abriuJanela, useJanelasAbertas } from '../lib/telasAbertas';
import { useTaskSheet } from './TaskSheet';
import { ActionSheet, Sheet, type Action } from './ui';
import { PickerModal } from './Pickers';
import { C, aba, alpha, useCores } from './theme';
import { addDays, brYmd, horaTela, parseYmd, today } from '../lib/date';
import { t } from '../lib/i18n';

const nomeAba = (a: AbaId) => aba(a)?.nome ?? '';
const quandoTxt = (data?: string | null, hora?: string | null) => [data ? brYmd(data.slice(0, 10)) : '', hora ? horaTela(hora) : ''].filter(Boolean).join(' · ');

export function EnvioPlannerHost() {
  const { accent } = useCores();
  const sheet = useTaskSheet();
  const pedido = usePedidoEnvio();
  const avisoChaves = useAvisoEnvio();
  const listaAberta = useListaEnvios();
  const confirmar = usePedidoConfirmar();
  const docEnvios = useDoc('envios');
  const janelas = useJanelasAbertas();
  const [lembrar, setLembrar] = useState(false);
  const [quando, setQuando] = useState<string | null>(null);        // chave do pendente escolhendo "Escolher quando"
  const [dataQuando, setDataQuando] = useState<string | null>(null);
  const [dataConf, setDataConf] = useState<{ aba: AbaId; id: string } | null>(null);   // "Me lembre de confirmar" > Escolher a data
  const [vez, setVez] = useState<PedidoEnvio | null>(null);   // a pergunta na tela (fica até a resposta)

  // E2: confere ao abrir o app e ao voltar para ele
  useEffect(() => {
    const tm = setTimeout(() => conferirAvisoEnvio(), 5000);
    const sub = AppState.addEventListener('change', s => { if (s === 'active') setTimeout(() => conferirAvisoEnvio(), 1500); });
    return () => { clearTimeout(tm); sub.remove(); };
  }, []);

  // aviso: some sozinho em ~10 s (vale como "Me lembre depois")
  const avisoVivo = avisoChaves.filter(k => !!docEnvios?.pend?.[k]);
  useEffect(() => {
    if (!avisoChaves.length) return;
    const tm = setTimeout(fecharAvisoEnvio, 10000);
    return () => clearTimeout(tm);
  }, [avisoChaves]);

  // "Lembrar minha escolha nesta aba": "Enviar" vai direto para a página de agendar
  const abrirAgendar = (p: PedidoEnvio) => setTimeout(() => enviarAgora(p, sheet.open), 400);
  useEffect(() => {
    if (pedido?.direto && !janelas) { const p = pedido.p; fecharPedidoEnvio(); abrirAgendar(p); }
  }, [pedido, janelas]); // eslint-disable-line react-hooks/exhaustive-deps

  // a pergunta espera as outras janelas fecharem (no iPhone, uma janela aberta por cima de outra não aparece)
  useEffect(() => {
    if (!pedido) setVez(null);
    else if (!pedido.direto && !janelas && vez !== pedido.p) { setLembrar(false); setVez(pedido.p); }
  }, [pedido, janelas]); // eslint-disable-line react-hooks/exhaustive-deps
  const mostrarPergunta = !!vez && !!pedido;
  useEffect(() => (mostrarPergunta ? abriuJanela() : undefined), [mostrarPergunta]);

  const responder = (r: 'enviar' | 'depois' | 'nao') => {
    if (!pedido) return;
    const p = pedido.p;
    if (lembrar) lembrarEscolha(p.aba, r);
    fecharPedidoEnvio();
    if (r === 'enviar') abrirAgendar(p);
    else if (r === 'depois') depois(p);
  };

  const pendDe = (k: string): PedidoEnvio | null => { const x = docEnvios?.pend?.[k]; return x ? { aba: x.aba, id: x.id, titulo: x.titulo, emoji: x.emoji, data: x.data, hora: x.hora, prof: x.prof } : null; };
  const enviarPend = (k: string) => { const p = pendDe(k); fecharAvisoEnvio(); abrirListaEnvios(false); if (p) abrirAgendar(p); };
  const naoPend = (k: string) => encerrarPendente(k);

  const opcoesQuando: Action[] | null = quando ? [
    { label: t('Daqui a 1 hora'), onPress: () => escolherQuando(quando, new Date(Date.now() + 3600000)) },
    { label: t('Amanhã'), onPress: () => escolherQuando(quando, as9(addDays(today(), 1))) },
    { label: t('Daqui a 3 dias'), onPress: () => escolherQuando(quando, as9(addDays(today(), 3))) },
    { label: t('Daqui a 1 semana'), onPress: () => escolherQuando(quando, as9(addDays(today(), 7))) },
    { label: '📅 ' + t('Escolher a data'), onPress: () => { const k = quando; setTimeout(() => setDataQuando(k), 350); } },
  ] : null;

  const pend = Object.entries(docEnvios?.pend ?? {}).filter(([, p]) => existe(p.aba, p.id));
  const aguardando = Object.values(docEnvios?.aguardando ?? {}).filter(a => existe(a.aba, a.id));
  const escolhas = Object.entries(docEnvios?.escolha ?? {}) as [AbaId, 'enviar' | 'depois' | 'nao'][];
  const nomeEscolha = { enviar: t('Enviar agora'), depois: t('Me lembre depois'), nao: t('Não, obrigada') };
  const um = avisoVivo.length === 1 ? docEnvios?.pend?.[avisoVivo[0]] : null;

  return (<>
    {/* E1: a pergunta */}
    <Modal visible={mostrarPergunta} transparent animationType="fade" onRequestClose={() => responder('depois')}>
      <View style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'center', padding: 24 }}>
        <View style={{ backgroundColor: C.card, borderRadius: 20, padding: 18, gap: 12, maxWidth: 460, width: '100%', alignSelf: 'center' }}>
          <Text style={{ fontSize: 26, textAlign: 'center' }}>📅</Text>
          {!!pedido && <Text style={{ color: C.sub, fontSize: 13, textAlign: 'center' }} numberOfLines={2}>{pedido.p.emoji ? pedido.p.emoji + ' ' : ''}{pedido.p.titulo}{pedido.p.data ? ` · ${quandoTxt(pedido.p.data, pedido.p.hora)}` : ''}</Text>}
          <Text style={{ color: C.text, fontSize: 16, fontWeight: '700', textAlign: 'center' }}>{t('Isto só aparece no My Planner se você enviar. Quer enviar agora?')}</Text>
          <BotaoCheio cor={accent} label={t('Enviar agora')} onPress={() => responder('enviar')} />
          <BotaoLinha cor={accent} label={t('Me lembre depois')} onPress={() => responder('depois')} />
          <BotaoLinha cor={C.sub} label={t('Não, obrigada')} onPress={() => responder('nao')} />
          <Pressable onPress={() => setLembrar(x => !x)} hitSlop={8} accessibilityRole="checkbox" accessibilityState={{ checked: lembrar }}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 8, justifyContent: 'center', marginTop: 2 }}>
            <View style={{ width: 20, height: 20, borderRadius: 5, borderWidth: 2, borderColor: lembrar ? accent : C.mute, backgroundColor: lembrar ? accent : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
              {lembrar && <Text style={{ color: '#fff', fontSize: 12, fontWeight: '900' }}>✓</Text>}
            </View>
            <Text style={{ color: C.sub, fontSize: 13 }}>{t('Lembrar minha escolha nesta aba')}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>

    {/* E2: o aviso pequeno embaixo da tela (não cobre a tela; some sozinho) */}
    {avisoVivo.length > 0 && (
      <View pointerEvents="box-none" style={{ position: 'absolute', left: 12, right: 12, bottom: 96, alignItems: 'center' }}>
        <View style={{ backgroundColor: C.card2, borderRadius: 14, padding: 12, gap: 8, borderWidth: 1, borderColor: alpha(accent, 0.5), maxWidth: 520, width: '100%',
          shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 8, elevation: 6 }}>
          {um ? (<>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ color: C.text, flex: 1, fontSize: 13 }} numberOfLines={2}>📅 {t('Você ainda não enviou {titulo} para o My Planner.', { titulo: um.titulo })}</Text>
              <Pressable onPress={fecharAvisoEnvio} hitSlop={10} accessibilityLabel={t('Fechar')}><Text style={{ color: C.sub, fontSize: 15 }}>✕</Text></Pressable>
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <Mini cor={accent} cheio label={t('Enviar agora')} onPress={() => enviarPend(avisoVivo[0])} />
              <Mini cor={accent} label={t('Me lembre depois')} onPress={fecharAvisoEnvio} />
              <Mini cor={C.sub} label={t('Não, obrigada')} onPress={() => { naoPend(avisoVivo[0]); fecharAvisoEnvio(); }} />
              <Pressable onPress={() => { const k = avisoVivo[0]; fecharAvisoEnvio(); setQuando(k); }} hitSlop={6}>
                <Text style={{ color: C.sub, fontSize: 11, textDecorationLine: 'underline' }}>{t('Escolher quando')}</Text>
              </Pressable>
            </View>
          </>) : (
            <Pressable onPress={() => { fecharAvisoEnvio(); abrirListaEnvios(); }} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ color: C.text, flex: 1, fontWeight: '700' }}>📅 {t('{n} itens aguardando envio', { n: avisoVivo.length })}</Text>
              <Text style={{ color: accent, fontWeight: '800' }}>{t('Ver')} ›</Text>
              <Pressable onPress={fecharAvisoEnvio} hitSlop={10} accessibilityLabel={t('Fechar')}><Text style={{ color: C.sub, fontSize: 15 }}>✕</Text></Pressable>
            </Pressable>
          )}
        </View>
      </View>
    )}

    {/* lista dos itens aguardando envio */}
    <Sheet visible={listaAberta} onClose={() => abrirListaEnvios(false)} title={t('Aguardando envio ao My Planner')}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 40 }}>
        <Text style={{ color: C.sub, fontSize: 13 }}>{t('Nada de outras abas vai sozinho para o My Planner. Estes itens têm data e ainda não foram enviados.')}</Text>
        {!pend.length && <Text style={{ color: C.mute }}>{t('Nenhum item aguardando envio.')}</Text>}
        {pend.map(([k, p]) => (
          <View key={k} style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 8 }}>
            <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={2}>{p.emoji ? p.emoji + ' ' : ''}{p.titulo}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{[nomeAba(p.aba), quandoTxt(p.data, p.hora), p.quando ? '🔔 ' + new Date(p.quando).toLocaleString() : ''].filter(Boolean).join(' · ')}</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <Mini cor={accent} cheio label={t('Enviar agora')} onPress={() => enviarPend(k)} />
              <Mini cor={accent} label={t('Me lembre depois')} onPress={() => lembrarDepois(k)} />
              <Mini cor={C.sub} label={t('Não, obrigada')} onPress={() => naoPend(k)} />
              <Pressable onPress={() => setQuando(k)} hitSlop={6}><Text style={{ color: C.sub, fontSize: 11, textDecorationLine: 'underline' }}>{t('Escolher quando')}</Text></Pressable>
            </View>
          </View>
        ))}
        {aguardando.length > 0 && <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800', marginTop: 8 }}>⏳ {t('Aguardando confirmação').toUpperCase()}</Text>}
        {aguardando.map(a => (
          <View key={a.aba + a.id} style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 6 }}>
            <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={2}>{a.titulo}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{[nomeAba(a.aba), a.lembrar ? '🔔 ' + new Date(a.lembrar).toLocaleString() : ''].filter(Boolean).join(' · ')}</Text>
            <View style={{ flexDirection: 'row', gap: 6 }}>
              <Mini cor={accent} cheio label={'✅ ' + t('Confirmar')} onPress={() => confirmarItem(a.aba, a.id)} />
              <Mini cor={accent} label={'🔔 ' + t('Me lembre de confirmar')} onPress={() => pedirDataConfirmar({ aba: a.aba, id: a.id })} />
            </View>
          </View>
        ))}
        {escolhas.length > 0 && (
          <View style={{ gap: 6, marginTop: 8 }}>
            <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('Escolhas lembradas').toUpperCase()}</Text>
            {escolhas.map(([ab, e]) => (
              <View key={ab} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Text style={{ color: C.text, flex: 1 }}>{nomeAba(ab)} · {nomeEscolha[e]}</Text>
                <Pressable onPress={() => lembrarEscolha(ab, null)} hitSlop={8}><Text style={{ color: accent, fontWeight: '700', fontSize: 12 }}>{t('Esquecer')}</Text></Pressable>
              </View>
            ))}
          </View>
        )}
      </ScrollView>
    </Sheet>

    <ActionSheet title={t('Escolher quando')} actions={opcoesQuando} onClose={() => setQuando(null)} />
    {!!dataQuando && <PickerModal mode="date" cor={accent} value={parseYmd(addDays(today(), 1))} onClose={() => setDataQuando(null)}
      onPick={d => { const k = dataQuando; setDataQuando(null); const x = new Date(d); x.setHours(9, 0, 0, 0); escolherQuando(k, x); }} />}

    {/* E3: "Me lembre de confirmar": escolhe o dia (o celular avisa às 9h) */}
    <ActionSheet title={t('Me lembre de confirmar')} actions={confirmar ? [
      { label: t('Amanhã'), onPress: () => { lembrarDeConfirmar(confirmar.aba, confirmar.id, as9(addDays(today(), 1))); pedirDataConfirmar(null); } },
      { label: t('Daqui a 3 dias'), onPress: () => { lembrarDeConfirmar(confirmar.aba, confirmar.id, as9(addDays(today(), 3))); pedirDataConfirmar(null); } },
      { label: t('Daqui a 1 semana'), onPress: () => { lembrarDeConfirmar(confirmar.aba, confirmar.id, as9(addDays(today(), 7))); pedirDataConfirmar(null); } },
      { label: '📅 ' + t('Escolher a data'), onPress: () => { const c = confirmar; setTimeout(() => setDataConf(c), 350); } },
      ...(docEnvios?.aguardando?.[`${confirmar.aba}:${confirmar.id}`]?.lembrar ? [{ label: t('Não lembrar'), onPress: () => { lembrarDeConfirmar(confirmar.aba, confirmar.id, null); pedirDataConfirmar(null); } }] : []),
    ] : null} onClose={() => pedirDataConfirmar(null)} />
    {!!dataConf && <PickerModal mode="date" cor={accent} value={parseYmd(addDays(today(), 1))} onClose={() => setDataConf(null)}
      onPick={d => { const x = new Date(d); x.setHours(9, 0, 0, 0); lembrarDeConfirmar(dataConf.aba, dataConf.id, x); setDataConf(null); }} />}
  </>);
}

const as9 = (ymd: string) => { const d = parseYmd(ymd); d.setHours(9, 0, 0, 0); return d; };

function BotaoCheio({ label, onPress, cor }: { label: string; onPress: () => void; cor: string }) {
  return <Pressable onPress={onPress} style={{ backgroundColor: cor, borderRadius: 12, paddingVertical: 12, alignItems: 'center' }}><Text style={{ color: '#fff', fontWeight: '800' }}>{label}</Text></Pressable>;
}
function BotaoLinha({ label, onPress, cor }: { label: string; onPress: () => void; cor: string }) {
  return <Pressable onPress={onPress} style={{ borderRadius: 12, paddingVertical: 11, alignItems: 'center', borderWidth: 1, borderColor: alpha(cor.startsWith('#') ? cor : '#888888', 0.6) }}><Text style={{ color: cor, fontWeight: '700' }}>{label}</Text></Pressable>;
}
function Mini({ label, onPress, cor, cheio }: { label: string; onPress: () => void; cor: string; cheio?: boolean }) {
  return (
    <Pressable onPress={onPress} hitSlop={4} style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 10, backgroundColor: cheio ? cor : 'transparent', borderWidth: 1, borderColor: cor }}>
      <Text style={{ color: cheio ? '#fff' : cor, fontWeight: '700', fontSize: 12 }}>{label}</Text>
    </Pressable>
  );
}

/** No My Planner de hoje: a lista de itens que esperam envio (os que já apareceram 3 vezes ficam só aqui). */
export function AguardandoEnvio() {
  const d = useDoc('envios');
  const { accent } = useCores();
  const n = Object.values(d?.pend ?? {}).filter(p => existe(p.aba, p.id)).length + Object.values(d?.aguardando ?? {}).filter(a => existe(a.aba, a.id)).length;
  if (!n) return null;
  return (
    <Pressable onPress={() => abrirListaEnvios()} hitSlop={6} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 4 }}>
      <Text style={{ color: C.sub, fontSize: 12, flex: 1 }}>📨 {t('{n} item(ns) aguardando envio ao My Planner', { n })}</Text>
      <Text style={{ color: accent, fontSize: 12, fontWeight: '800' }}>{t('Ver')} ›</Text>
    </Pressable>
  );
}
